import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';

import { EpcotConfig, QuickBetConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { generateGuid, interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { SportsThemedButtonDirective } from '@frontend/sports/common/ui/sports-themed-button';
import { SyncUserSettingsService, UserService } from '@frontend/sports/user/feature';
import { DsButton, DsButtonKind } from '@frontend/ui/button';
import { Store } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { ArcRestrictionService } from 'packages/sports/web/app/src/arc/arc-restriction.service';
import { GeolocationCheckService } from 'packages/sports/web/app/src/geo-location/geo-location-check.service';
import { NumpadDisplayService } from 'packages/sports/web/app/src/numpad/numpad-display.service';
import { Observable, Subject, combineLatest, exhaustMap, map } from 'rxjs';

import { CurrencyPipe } from '../../../base/pipes/currency.pipe';
import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { betslipPlaceButtonStateIsValidSelector } from '../../betplacement/selectors';
import { PaymentMethod } from '../../quick-deposit/quick-deposit.models';
import { selectQuickDepositSelectedPaymentMethod } from '../../quick-deposit/selector';
import { IRewardToken, RewardTokenContext } from '../../reward-tokens/reward-tokens.model';
import { selectHasEachWayOptionPick } from '../../reward-tokens/selectors';
import { isFreebetToken } from '../../reward-tokens/services/reward-tokens.utils';
import { getPossibleWinningsNet } from '../../summary/services/betslip-summary-builder.service';
import { BetslipSummaryService } from '../../summary/services/betslip-summary.service';
import { BetslipTaxationLiabilityComponent } from '../../taxation/components/betslip-taxation-liability.component';
import { QuickBetActions } from '../quick-bet-actions';
import { selectClosedLockedPickOverview, selectQuickBetErrorsStateWithRewardsTokens, selectQuickBetRewardToken } from '../quick-bet.selectors';
import { IQuickBetActionButtonState, IQuickBetState, QuickBetActionButtonState } from '../quick-bet.state';
import { QuickBetUtils } from '../services/quick-bet-utils.service';

interface UiQuickBetActionButtonState {
    button: IQuickBetActionButtonState;
    quickBet: IQuickBetState;
    isEachWay: boolean;
    selectedPaymentMethod?: PaymentMethod;
    selectedToken: Nullable<IRewardToken>;
    closedPickCount: number;
    lockedPickCount: number;
}

@Component({
    selector: 'bs-quick-bet-action-button',
    imports: [CommonModule, DsButton, BetslipTaxationLiabilityComponent, CurrencyPipe, SportsThemedButtonDirective],
    templateUrl: './quick-bet-action-button.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./quick-bet-action-button.component.scss'],
})
export class QuickBetActionButtonComponent implements OnInit, OnDestroy {
    @Input() rewardTokenContext: RewardTokenContext;

    isInfoVisible = false;
    epcotEnabled = this.epcotConfig.isEnabled;
    buttonStates = QuickBetActionButtonState;

    private clickActionSubject$ = new Subject<QuickBetActionButtonState>();

    taxInfoMessage: string;
    paymentMethod = PaymentMethod;

    constructor(
        public sitecore: Sitecore,
        public quickBetConfig: QuickBetConfig,
        private quickBetUtils: QuickBetUtils,
        private summaryService: BetslipSummaryService,
        private userService: UserService,
        private moneyService: MoneyService,
        private geolocationCheckService: GeolocationCheckService,
        private store: Store<IBetslipRootState>,
        private arcRestrictionService: ArcRestrictionService,
        private changeRef: ChangeDetectorRef,
        private userSettings: SyncUserSettingsService,
        private numpadDisplayService: NumpadDisplayService,
        private epcotConfig: EpcotConfig,
    ) {}

    getUiActionButtonState$: Observable<UiQuickBetActionButtonState>;

    ngOnInit(): void {
        const summary$ = this.summaryService.getSummary$();

        const state$ = this.store.select(betslipSelector);
        const eachWayState$ = this.store.select(selectHasEachWayOptionPick);
        const selectQuickDepositSelectedPaymentMethod$ = this.store.select(selectQuickDepositSelectedPaymentMethod);

        const selectedRewardTokenState$ = this.store.select(selectQuickBetRewardToken);
        const errorsStatus$ = this.store.select(selectQuickBetErrorsStateWithRewardsTokens);

        const stateChanged$ = combineLatest([
            summary$,
            state$,
            eachWayState$,
            selectedRewardTokenState$,
            errorsStatus$,
            selectQuickDepositSelectedPaymentMethod$,
        ]).pipe(
            map(([summary, state, isEachWay, selectedToken, errorsStatus, selectedPaymentMethod]) => {
                const actionButtonState = this.quickBetUtils.getActionButtonState(summary, state, errorsStatus, this.rewardTokenContext);

                return {
                    button: actionButtonState,
                    quickBet: state.quickBet,
                    isEachWay,
                    selectedPaymentMethod,
                    selectedToken,
                };
            }),
        );

        const buttonIsValidState$ = this.store.select(betslipPlaceButtonStateIsValidSelector);
        const closedLockedPickOverview$ = this.store.select(selectClosedLockedPickOverview);

        this.getUiActionButtonState$ = combineLatest([
            stateChanged$,
            this.arcRestrictionService.isUserOnPlayBreak$,
            buttonIsValidState$,
            closedLockedPickOverview$,
        ]).pipe(
            map(([uiState, isOnPlayBreak, isValid, { typeState, pickList, comboPickErrors }]) => {
                const { closedPickCount, lockedPickCount } = this.quickBetUtils.getClosedAndLockedPickCount(typeState, pickList, comboPickErrors);

                return {
                    button: {
                        ...uiState.button,
                        isDisabled: uiState.button.isDisabled || isOnPlayBreak || !isValid,
                    },
                    quickBet: uiState.quickBet,
                    isEachWay: uiState.isEachWay,
                    selectedToken: uiState.selectedToken,
                    selectedPaymentMethod: uiState.selectedPaymentMethod,
                    closedPickCount,
                    lockedPickCount,
                };
            }),
        );

        this.clickActionSubject$
            .pipe(
                exhaustMap((buttonState) => {
                    return new Observable((subscriber) => {
                        try {
                            this.geolocationCheckService.checkGeolocation(
                                (isLocationFailed) => {
                                    if (!isLocationFailed) {
                                        this.quickBet(buttonState);
                                        subscriber.next('You win');
                                    } else {
                                        this.store.dispatch(QuickBetActions.setActionButton({ payload: buttonState }));
                                        subscriber.next('You failed');
                                    }
                                    subscriber.complete();
                                },
                                { waitForLocationAvailable: true },
                            );
                        } catch {
                            subscriber.complete();
                        }
                    });
                }),
            )
            .subscribe(() => this.changeRef.detectChanges());
    }

    ngOnDestroy(): void {
        this.clickActionSubject$.complete();
    }

    onButtonClick(state: QuickBetActionButtonState): void {
        this.numpadDisplayService.close();
        this.setPlaceButton(state);
        this.clickActionSubject$.next(state);
    }

    quickBet(state: QuickBetActionButtonState): void {
        this.numpadDisplayService.close();
        this.store.dispatch(QuickBetActions.actionButtonClick({ payload: { state, requestId: generateGuid() } }));
    }

    getButtonKind(state: QuickBetActionButtonState): DsButtonKind {
        switch (state) {
            case QuickBetActionButtonState.Login:
            case QuickBetActionButtonState.ProcessingDeposit:
            case QuickBetActionButtonState.MakeDeposit:
            case QuickBetActionButtonState.UpdateMinStake:
            case QuickBetActionButtonState.Deposit:
            case QuickBetActionButtonState.AcceptChanges:
                return 'primary';
            default:
                return 'success';
        }
    }

    getButtonThemeKind(): ThemeOptions<DsButtonKind> {
        return { 'mgm-4': 'primary', 'sports-interaction': 'primary' };
    }

    getButtonTitle(state: QuickBetActionButtonState): string {
        switch (state) {
            case QuickBetActionButtonState.Login:
                return this.sitecore.quickBetButtonStates.Login;
            case QuickBetActionButtonState.MakeDeposit:
                return this.sitecore.quickBetButtonStates.MakeDeposit;
            case QuickBetActionButtonState.Deposit:
                return this.sitecore.quickBetButtonStates.Deposit;
            case QuickBetActionButtonState.DepositAndPlaceBet:
                return this.sitecore.quickBetButtonStates.DepositAndPlaceBet;
            case QuickBetActionButtonState.Place:
                return this.sitecore.quickBetButtonStates.PlaceBet;
            case QuickBetActionButtonState.PlaceFreeBet:
                return this.sitecore.quickBetButtonStates.PlaceFreeBet;
            case QuickBetActionButtonState.AcceptChanges:
                return this.sitecore.quickBetButtonStates.AcceptChanges;
            case QuickBetActionButtonState.AcceptAndPlace:
                return this.sitecore.quickBetButtonStates.AcceptChangesAndPlaceBet;
            case QuickBetActionButtonState.AcceptAndPlaceFreeBet:
                return this.sitecore.quickBetButtonStates.AcceptChangesAndPlaceFreeBet;
            case QuickBetActionButtonState.Placing:
                return this.sitecore.quickBetButtonStates.ProcessingBet;
            case QuickBetActionButtonState.PlacingFreeBet:
                return this.sitecore.quickBetButtonStates.ProcessingFreeBet;
            case QuickBetActionButtonState.ProcessingDeposit:
                return this.sitecore.quickBetButtonStates.ProcessingDeposit;
            case QuickBetActionButtonState.ProcessingDepositAndBet:
                return this.sitecore.quickBetButtonStates.ProcessingDepositAndBet;
            case QuickBetActionButtonState.UpdateMinStake:
                return this.sitecore.betslip.messages.UpdateMinStake;
            default:
                return '';
        }
    }

    getPossibleWinnings(state: IQuickBetActionButtonState): Nullable<Decimal> {
        return this.userSettings.isTurnoverTaxApplied && state.possibleWinnings?.greaterThan(this.userSettings.configSettings.winningsTaxThreshold)
            ? state.possibleWinningsNet
            : getPossibleWinningsNet(state.possibleWinnings, state.taxationAmount, state.winningsBoost);
    }

    getTaxationLiability(state: IQuickBetActionButtonState): Nullable<Decimal> {
        return state.taxationLiability?.greaterThan(0) ? state.taxationLiability : null;
    }

    getStake(state: IQuickBetState, isEachWay: boolean, selectedToken: Nullable<IRewardToken>): string | undefined {
        if (isFreebetToken(selectedToken)) {
            return this.moneyService.transform(selectedToken.amount, true);
        }

        let stake = state.stakeState.stake;

        if (Number(stake) === 0) {
            return;
        }

        if (!!stake && isEachWay) {
            const numberStake = Number(stake);
            const eachWayStake = numberStake * 2;

            stake = eachWayStake.toString();
        }

        return this.moneyService.transform(stake, true);
    }

    showStake(state: QuickBetActionButtonState): boolean {
        return (
            this.quickBetConfig.isTotalReturnsEnabled &&
            state !== this.buttonStates.AcceptChanges &&
            state !== this.buttonStates.MakeDeposit &&
            state !== this.buttonStates.UpdateMinStake &&
            state !== this.buttonStates.Login
        );
    }

    getButtonMessage(
        quickBetState: IQuickBetState,
        actionButtonState: IQuickBetActionButtonState,
        closedPickCount: number,
        lockedPickCount: number,
    ): Nullable<string> {
        if (actionButtonState.state === QuickBetActionButtonState.AcceptChanges && quickBetState.hasClosedPick && quickBetState.hasLockedPick) {
            return interpolateDoubleBracesString(this.sitecore.betslip.messages.LockedBetsToRemove, { count: closedPickCount + lockedPickCount });
        }
        if (actionButtonState.state === QuickBetActionButtonState.AcceptChanges && quickBetState.hasClosedPick) {
            return interpolateDoubleBracesString(this.sitecore.betslip.linear.ClosedBetsToRemove, { count: closedPickCount });
        }
        if (actionButtonState.state === QuickBetActionButtonState.AcceptChanges && quickBetState.hasLockedPick) {
            return interpolateDoubleBracesString(this.sitecore.quickBetMessages.RemoveLockedPick, { count: lockedPickCount });
        }
        if (actionButtonState.state === QuickBetActionButtonState.UpdateMinStake) {
            const minStakeWithCurrency = {
                amountWithCurrency: this.moneyService.transform(this.userSettings.configSettings.minimumStake, true),
            };

            return interpolateDoubleBracesString(this.sitecore.betslip.validation.MinStakeValue, minStakeWithCurrency);
        }

        return null;
    }

    getPossibleWinningsLabel(state: IQuickBetActionButtonState): string {
        return state.hasTaxation ? this.sitecore.quickBetButtonStates.PossibleNetWinnings : this.sitecore.quickBetButtonStates.PossibleWinnings;
    }

    get isZaTaxationVisible(): boolean {
        return !this.quickBetConfig.isTotalReturnsEnabled && this.userService.country === 'ZA';
    }

    setPlaceButton(state: QuickBetActionButtonState): void {
        switch (state) {
            case QuickBetActionButtonState.AcceptAndPlace:
            case QuickBetActionButtonState.Place:
                return this.store.dispatch(QuickBetActions.setActionButton({ payload: QuickBetActionButtonState.Placing }));
            case QuickBetActionButtonState.AcceptAndPlaceFreeBet:
            case QuickBetActionButtonState.PlaceFreeBet:
                return this.store.dispatch(QuickBetActions.setActionButton({ payload: QuickBetActionButtonState.PlacingFreeBet }));
            default:
                return;
        }
    }
}
